import { clone, difference, isArray, isEmpty, isNil, trim } from 'lodash-es';
import { computed, watch, onUnmounted, unref } from 'vue';
import { useStore } from 'vuex';
import { normalizeStringList } from '@/utils/string';

/**
 * 归一化 fullCode 数组（去空、去重、字典序排序，便于对拍）。
 * @param {unknown[]} list 原始入参
 * @returns {string[]}
 */
function normalizeCodesList(list) {
  return normalizeStringList(list, {
    sort: true,
    mapItem: (item) => unref(item)
  });
}

/**
 * 从 `fullCodesRef` 解析出当前应订阅的 fullCode 列表（必须为数组形态）。
 * @param {unknown} input fullCodesRef 解开值
 * @returns {string[]}
 */
function normalizeCodesRefInput(input) {
  const raw = unref(input);
  if (isNil(raw)) {
    return [];
  }
  if (!isArray(raw)) {
    console.warn('[useStockRealtimeSnapshot] fullCodesRef 须为 string[] 或 Ref<string[]> / ComputedRef<string[]>');
    return [];
  }
  return normalizeCodesList(raw);
}

/**
 * 将入参规范为 fullCode 字符串数组（用于手动 `subscribe` / `unsubscribe`）。
 * @param {string | string[] | import('vue').Ref<string> | import('vue').ComputedRef<string> | import('vue').Ref<string[]>} codes
 * @returns {string[]}
 */
function normalizeToFullCodes(codes) {
  const raw = unref(codes);
  if (isNil(raw)) {
    return [];
  }
  if (isArray(raw)) {
    return normalizeCodesList(raw);
  }
  const s = trim(String(raw));
  return s ? [s] : [];
}

/**
 * 多标的 WebSocket 实时快照：按 `fullCodesRef`（**仅数组**）自动增减订阅，数据来自 `stock.snapshotsByFullCode`。
 *
 * - **`options.fields` 必传**：业务侧需要的字段集合（如 `['lastPrice', 'changePct']`），由底层映射到对应 channel；
 *   缺省视为参数错误，hook 会发出 warn 并跳过本次订阅，避免出现「订阅了但不知道订阅什么」的隐式行为。
 * - **自动模式**：`fullCodesRef` 为 `string[]` / `Ref<string[]>` / `ComputedRef<string[]>`；变更时只对增删的 code dispatch；卸载时退订当前列表。
 * - **`manual: true`**：不进行自动 watch / 卸载退订，仅由 `subscribe` / `unsubscribe` 负责生命周期。
 * - 单标的请传入 **单元素数组**，例如 `ref(['SH.600519'])`。
 *
 * @param {import('vue').Ref<string[]> | import('vue').ComputedRef<string[]> | string[] | undefined} fullCodesRef 订阅的 fullCode 列表
 * @param {{ fields: string[], manual?: boolean }} options 订阅配置；`fields` 必传
 * @returns {{
 *   activeCodes: import('vue').ComputedRef<string[]>,
 *   snapshotsMap: import('vue').ComputedRef<Record<string, { quote: Record<string, unknown> | null }>>,
 *   subscribe: (codes: string | string[] | import('vue').Ref<string> | import('vue').ComputedRef<string> | import('vue').Ref<string[]>) => void,
 *   unsubscribe: (codes: string | string[] | import('vue').Ref<string> | import('vue').ComputedRef<string> | import('vue').Ref<string[]>) => void
 * }}
 * @example
 * const { snapshotsMap } = useStockRealtimeSnapshot(
 *   computed(() => props.fullCodes),
 *   { fields: ['lastPrice', 'changePct'] }
 * );
 *
 * @example
 * const { subscribe, unsubscribe } = useStockRealtimeSnapshot(undefined, {
 *   fields: ['lastPrice', 'changePct'],
 *   manual: true
 * });
 */
export function useStockRealtimeSnapshot(fullCodesRef, options = {}) {
  const { manual = false, fields } = options || {};
  const store = useStore();

  // fields 必传校验：缺省时 hook 全程 no-op，但仍返回完整接口以避免调用方崩溃
  const fieldsValid = isArray(fields) && !isEmpty(fields);
  if (!fieldsValid) {
    console.warn('[useStockRealtimeSnapshot] options.fields 必传（如 ["lastPrice", "changePct"]）');
  }

  const activeCodes = computed(() => normalizeCodesRefInput(fullCodesRef));

  /**
   * 手动增加订阅引用（可传单个 fullCode 或数组）。
   * @param {string | string[] | import('vue').Ref<string> | import('vue').ComputedRef<string> | import('vue').Ref<string[]>} codes
   * @returns {void}
   */
  const subscribe = (codes) => {
    if (!fieldsValid) {
      // fields 缺省时禁止任何订阅，避免 manager 侧出现匹配不到 channel 的告警
      return;
    }
    const fullCodes = normalizeToFullCodes(codes);
    if (!fullCodes.length) {
      return;
    }
    store.dispatch('stock/subscribeCodes', { fullCodes, fields });
  };

  /**
   * 手动减少订阅引用（可传单个 fullCode 或数组）。
   * @param {string | string[] | import('vue').Ref<string> | import('vue').ComputedRef<string> | import('vue').Ref<string[]>} codes
   * @returns {void}
   */
  const unsubscribe = (codes) => {
    if (!fieldsValid) {
      return;
    }
    const fullCodes = normalizeToFullCodes(codes);
    if (!fullCodes.length) {
      return;
    }
    store.dispatch('stock/unsubscribeCodes', { fullCodes, fields });
  };

  if (!manual && fieldsValid) {
    // 自动模式：监听 fullCodes 变化做差量增删；空集合自动收敛
    watch(
      activeCodes,
      (next, prev) => {
        const prevArr = isArray(prev) ? clone(prev) : [];
        const nextArr = isArray(next) ? clone(next) : [];
        const toUnsub = difference(prevArr, nextArr);
        const toSub = difference(nextArr, prevArr);
        if (toUnsub.length) {
          store.dispatch('stock/unsubscribeCodes', { fullCodes: toUnsub, fields });
        }
        if (toSub.length) {
          store.dispatch('stock/subscribeCodes', { fullCodes: toSub, fields });
        }
      },
      { deep: true, immediate: true }
    );
  }

  const snapshotsMap = computed(() => {
    /** @type {Record<string, { quote: Record<string, unknown> | null }>} */
    const out = {};
    // 仅暴露 quote 子树；后续若需要 core 等其它命名空间，再按需扩展
    for (const code of activeCodes.value) {
      const node = store.state.stock.snapshotsByFullCode[code];
      out[code] = {
        quote: node?.quote ?? null
      };
    }
    return out;
  });

  onUnmounted(() => {
    if (manual || !fieldsValid) {
      // 手动模式或 fields 无效：交给调用方负责生命周期，避免误退订
      return;
    }
    const codes = activeCodes.value;
    if (codes.length) {
      store.dispatch('stock/unsubscribeCodes', { fullCodes: clone(codes), fields });
    }
  });

  return { activeCodes, snapshotsMap, subscribe, unsubscribe };
}
